import { NetworkStatus, QueryHookOptions, useQuery } from '@apollo/client'
import { useCallback, useMemo, useState } from 'react'

import { useAllPriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { OffersQuery } from '../queries/swarmx/OffersQuery'
import {
  OffersQuery as OffersQueryGQL,
  OffersQueryVariables,
} from '../types/swarmx/graphql'
import { normalizeAllOffers } from '../utils/dotc'
import useEffectCompare from 'src/common/hooks/helpers/useEffectCompare'

export const PAGE_LIMIT = 10

const useOffers = (
  options?: QueryHookOptions<OffersQueryGQL, OffersQueryVariables>
) => {
  const [hasMore, setHasMore] = useState(false)
  const limit = PAGE_LIMIT

  const filter = useMemo(
    () => ({
      ...options?.variables?.filter,
      ...(!options?.variables?.filter?.or && { id_not_in: ['0x6'] }),
      ...(!options?.variables?.filter?.depositAsset && {}),
      isAuth: true,
    }),
    [options?.variables?.filter]
  )

  useEffectCompare(() => setHasMore(false), [filter])

  const {
    data,
    loading,
    error,
    fetchMore: nativeFetchMore,
    networkStatus,
    refetch,
  } = useQuery(OffersQuery, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    ...options,
    variables: {
      limit: limit,
      skip: 0,
      ...options?.variables,
      filter: filter,
    },
  })

  if (hasMore && data?.offers?.length && data?.offers.length % limit !== 0) {
    setHasMore(false)
  }

  const { priceFeeds, isLoading: priceFeedsLoading } = useAllPriceFeeds()

  const offers = useMemo(() => {
    if (!priceFeeds || priceFeedsLoading) return []
    return normalizeAllOffers(data?.offers || [], priceFeeds)
  }, [data?.offers, priceFeeds, priceFeedsLoading])

  const fetchMore = useCallback(async () => {
    if (options?.skip || !hasMore || error) return null
    const currentLength = offers.length

    const offersResponse = await nativeFetchMore({
      variables: {
        ...options?.variables,
        skip: currentLength,
        limit: PAGE_LIMIT,
        filter: filter,
      },
    })

    if (offersResponse.data.offers.length < PAGE_LIMIT) {
      setHasMore(false)
    } else {
      setHasMore(true)
    }

    // setLimit(currentLength + offersResponse.data.offers.length)

    return offersResponse
  }, [
    error,
    filter,
    hasMore,
    nativeFetchMore,
    offers.length,
    options?.skip,
    options?.variables,
  ])

  return useMemo(
    () => ({
      offers,
      hasMore,
      loading:
        priceFeedsLoading ||
        loading ||
        ![
          NetworkStatus.ready,
          NetworkStatus.error,
          NetworkStatus.poll,
        ].includes(networkStatus),
      error,
      refetch,
      refetching: [NetworkStatus.poll].includes(networkStatus),
      fetchMore: fetchMore,
    }),
    [
      error,
      fetchMore,
      hasMore,
      loading,
      networkStatus,
      offers,
      priceFeedsLoading,
      refetch,
    ]
  )
}

export default useOffers
